import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import {
  COLOR_VOC_TYPE,
  LIST_IS_REQUIRED_CONTENT_OPTION,
  LIST_VOC_TYPE_OPTION,
} from "helpers/constans";
import { postReason, putReason } from "store/voc-reason/thunk";

export interface Props {
  id?: string;
  item?: any;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  onCloseClick?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

const VOCReasonForm = ({
  id = "",
  item,
  triggerRefresh,
  isShowDetail = false,
  onCloseClick
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      const data = {
        status: values?.status,
        type: values?.type,
        reason_kr: values?.reason_kr,
        reason_en: values?.reason_en,
        sort: Number.parseInt(values?.sort),
        is_required_detail: values?.is_required_detail
      }

      const response: any = id
        ? await putReason(id, data)
        : await postReason(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    // type: Yup.string().required(`${t("VOC Type is required")}`),
    reason_en: Yup.string().required(`${t("Reason (EN) is required")}`),
    reason_kr: Yup.string().required(`${t("Reason (KR) is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      type: LIST_VOC_TYPE_OPTION?.[0]?.value,
      reason_en: "",
      reason_kr: "",
      is_required_detail: 1,
      sort: 1,
      status: 1,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleCloseToggle = () => {
    onCloseClick && onCloseClick();
  };

  useEffect(() => {
    if(id) {
      formik.setValues({
        ...item,
        is_required_detail: item?.is_required_content
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Create VOC Reason")} - ${t("VOC Management")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="l">
        <ModalHeader toggle={isLoading ? () => {} : handleCloseToggle}>
          {id ? t("Button Update Reason") : t("Button Create Reason")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 2) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              <form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("VOC Type")}
                      </Label>

                      <DropdownStatusNew
                        name="VOC_type"
                        dataList={LIST_VOC_TYPE_OPTION || []}
                        placeholder={`${t("Select VOC type")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.type}
                        onChangeSelect={(event: any) => {
                          formik.setFieldValue("type", event?.value);
                        }}
                        isHasOptionAll={false}
                        colors={COLOR_VOC_TYPE}
                      />
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="reason_en" className="form-label">
                        {t("Reason (EN)")}<span className="text-danger"> *</span>
                      </Label>
                      <Input
                        name="reason_en"
                        type="text"
                        className="form-control"
                        id="reason_en"
                        placeholder={t("Reason (EN)")}
                        value={formik?.values?.reason_en}
                        onChange={(event: any) =>
                          formik.setFieldValue("reason_en", event?.target?.value)
                        }
                      />
                      {formik.touched.reason_en && formik.errors.reason_en ? (
                        <div className="text-danger mt-2">
                          {formik.errors.reason_en}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="reason_kr" className="form-label">
                        {t("Reason (KR)")}<span className="text-danger"> *</span>
                      </Label>
                      <Input
                        name="reason_kr"
                        type="text"
                        className="form-control"
                        id="reason_kr"
                        placeholder={t("Reason (KR)")}
                        value={formik?.values?.reason_kr}
                        onChange={(event: any) =>
                          formik.setFieldValue("reason_kr", event?.target?.value)
                        }
                      />
                      {formik.touched.reason_kr && formik.errors.reason_kr ? (
                        <div className="text-danger mt-2">
                          {formik.errors.reason_kr}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="sort" className="form-label">
                        {t("Order")}
                      </Label>
                      <Input
                        name="sort"
                        type="number"
                        className="form-control"
                        placeholder={t("Enter sort")}
                        value={formik?.values?.sort}
                        onChange={(event: any) => {
                          formik.setFieldValue("sort", event?.target?.value)
                        }}
                      />
                      {formik.touched.sort && formik.errors.sort ? (
                        <div className="text-danger mt-2">{formik.errors.sort}</div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="titleKoreaInput" className="form-label">
                        {t("Is Require Detail")} <span className="text-danger"></span>
                      </Label>

                      <div className="d-flex justify-content-start align-items-center">
                        {
                          LIST_IS_REQUIRED_CONTENT_OPTION?.map((item) => {
                            return (
                              <div
                                className="d-flex justify-content-start align-items-center form-check me-3"
                                onClick={() => {
                                  formik.setFieldValue(
                                    "is_required_detail",
                                    item?.value
                                  );
                                }}
                              >
                                <input
                                  className="form-check-input me-2"
                                  type="radio"
                                  name="is_required_detail"
                                  value={item?.value}
                                  checked={item?.value === formik?.values?.is_required_detail}
                                  style={{ minWidth: "16px" }}
                                  onChange={(event) => {
                                    formik.setFieldValue(
                                      "is_required_detail",
                                      item?.value
                                    );
                                  }}
                                />
                                <label
                                  className="form-check-label position-relative"
                                  htmlFor={item?.label}
                                >
                                  {t(item?.label)}
                                </label>
                              </div>
                            )
                          })
                        }
                        
                      </div>
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">{t("Status")}</Label>

                      <div>
                        <div
                          className="form-check form-switch form-switch-sm text-center"
                          dir="ltr"
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="status"
                            checked={formik?.values?.status === 1 ? true : false}
                            onChange={(event) => {
                              formik.setFieldValue(
                                "status",
                                event?.target?.checked === true ? 1 : 0
                              );
                            }}
                          />
                        </div>
                      </div>

                      {formik?.touched?.status && formik?.errors?.status ? (
                        <div className="text-danger mt-2">
                          {/* {formik?.errors?.status} */}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mt-3">
                    {!isShowDetail && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id
                          ? t("Button Update")
                          : t("Button Create")}
                      </button>
                    )}
                  </div>
                </Col>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default VOCReasonForm;
